import { motion } from "framer-motion";
import { FaLinkedin, FaTwitter, FaGithub, FaFacebook } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { TeamCardSkeleton } from './Skeleton';
import PageTransition from './PageTransition';

// Team member data type
type TeamMember = {
  name: string;
  title: string;
  image: string;
  bio: string;
  social: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    facebook?: string; // Added facebook property to the type
  };
};

// Placeholder team data
const teamMembers: TeamMember[] = [
  {
    name: "Wobyeb Graphlain",
    title: "Founder/Team lead",
    image: "/images/team/sarah-chen.jpg",
    bio: "Tech enthusiast and entrepreneur with a passion for building innovative solutions.",
    social: {
      linkedin: "https://www.linkedin.com/in/wobyeb-graphlain-2296b423a?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
      facebook: "https://www.facebook.com/wobyeb.graphlain", // Fixed property name to match the type
    }
  },
  {
    name: "Dr Lawong Kendra",
    title: "Fack Checker/PhD",
    image: "/images/team/marcus-johnson.jpg",
    bio: "Awards winner, Researcher,community leader and Fact Checker.",
    social: {
      linkedin: "#",
      twitter: "#"
    }
  },
  {
    name: "Nduku Louis Tebi",
    title: "Mentor/Community Leader",
    image: "/images/team/aisha-patel.jpg",
    bio: "Advocate for youth empowerment,Peace education and digital literacy.",
    social: {
      linkedin: "#",
      twitter: "#"
    }
  },
  {
    name: "Idrisu Sharifatou",
    title: "Communications Lead",
    image: "/images/team/david-kim.jpg",
    bio: "A Muslim Girl, Advocate for peace, unity and harmony that is pasionate about Tech.",
    social: {
      linkedin: "#",
      github: "#"
    }
  },
  {
    name: "Ndzedzeni Lidovic",
    title: "Network Engineer",
    image: "/images/team/elena-rodriguez.jpg",
    bio: "A tech enthusiast, Network Engineer and a community leader.",
    social: {
      linkedin: "#",
      github: "#"
    }
  },
  {
    name: "Boyikih Albert",
    title: "Software Engineer",
    image: "/images/team/james-wilson.jpg",
    bio: "A tech enthusiast, Software Engineer from the University of Bamenda,Cameroon.",
    social: {
      linkedin: "#",
      twitter: "#"
    }
  }
];

const TeamSection = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <PageTransition>
      <section className="py-20 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent z-0" />
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px] animate-grid-flow z-0" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
              Our Team
            </div>
            <h2 className="text-4xl font-bold mb-4 text-white">Meet the Minds Behind Truth Nexus</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              A diverse team of experts dedicated to combating misinformation through innovation and education.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoading ? (
              // Show skeletons while loading
              Array(6).fill(0).map((_, index) => (
                <TeamCardSkeleton key={index} />
              ))
            ) : (
              // Show actual team cards
              teamMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="relative bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden transition-all duration-300 hover:border-truth/50 hover:shadow-[0_0_30px_rgba(0,255,255,0.1)]">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-truth/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Image container */}
                    <div className="relative h-64 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                      <p className="text-truth mb-3">{member.title}</p>
                      <p className="text-gray-400 text-sm mb-4">{member.bio}</p>
                      
                      {/* Social links */}
                      <div className="flex gap-3">
                        {member.social.linkedin && (
                          <a
                            href={member.social.linkedin}
                            className="text-gray-400 hover:text-truth transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaLinkedin className="w-5 h-5" />
                          </a>
                        )}
                        {member.social.facebook && (
                          <a
                            href={member.social.facebook}
                            className="text-gray-400 hover:text-truth transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaFacebook className="w-5 h-5" />
                          </a>
                        )}
                        {member.social.twitter && (
                          <a
                            href={member.social.twitter}
                            className="text-gray-400 hover:text-truth transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaTwitter className="w-5 h-5" />
                          </a>
                        )}
                        {member.social.github && (
                          <a
                            href={member.social.github}
                            className="text-gray-400 hover:text-truth transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaGithub className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>
    </PageTransition>
  );
};

export default TeamSection;
